import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lovable-demo/runtime/client";

/**
 * Governed escalation + management decision service.
 *
 * Escalations live on the governed Problem Record (record_id). Executive
 * Summary and Daily Briefing read these same rows — they never hold their own
 * copy. State changes go only through the escalation_acknowledge /
 * escalation_record_decision database functions, which check decision
 * authority and write the audit entry, history row and decision-time evidence
 * snapshot in one transaction (confirmed write: errors are thrown).
 * AI text is advisory only and is never written into the manager decision.
 */

export interface EvidenceSnapshot {
  capturedAt?: string;
  risk?: string;
  riskScore?: number;
  status?: string;
  exposure?: number;
  actualCost?: number;
  stagesComplete?: number;
  evidenceCount?: number;
  violatedClauses?: number;
  evidence?: { name: string; stage: string; addedAt?: string }[];
}

export interface EscalationTrigger {
  category: string;
  primary?: boolean;
  source: "rule" | "human" | string;
  rule: string;
  threshold: number | string;
  observed: number | string | null;
  level: number;
  at?: string;
}

export interface Escalation {
  id: string;
  record_id: string;
  record_code: string;
  reason: string;
  trigger_key: string;
  level: number;
  severity: string;
  status: string;
  raised_by_label: string;
  escalated_to_label: string;
  decision_required: string;
  ai_recommendation: string;
  evidence_snapshot: EvidenceSnapshot;
  decision_evidence_snapshot: EvidenceSnapshot;
  manager_decision: string;
  decision_rationale: string;
  resulting_actions: string[];
  decided_by_label: string;
  decided_at: string | null;
  acknowledged_at: string | null;
  due_at: string | null;
  created_at: string;
  primary_trigger_category: string;
  contributing_triggers: EscalationTrigger[];
  initiation_method: string;
  safety_regulatory: boolean;
  required_participants: string[];
  response_requirement: string;
  response_due_at: string | null;
  financial_exposure_at_escalation: number | null;
  actions_started_at: string | null;
  actions_completed_at: string | null;
  effectiveness_result: string;
  effectiveness_verified_at: string | null;
  residual_risk_acceptable: boolean | null;
  closure_rationale: string;
  closed_by_label: string;
  closed_at: string | null;
}

export interface EscalationEvent {
  id: string;
  escalation_id: string;
  record_id: string;
  record_code: string;
  event_type: string;
  from_status: string;
  to_status: string;
  actor_label: string;
  detail: string;
  occurred_at: string;
}

export const DECISION_OPTIONS = [
  "Approve",
  "Approve with conditions",
  "Reject",
  "Defer",
  "Redirect",
] as const;

/** Escalation Trigger & Authority Matrix — governed trigger categories. */
export const TRIGGER_CATEGORIES = [
  "Safety / Regulatory",
  "Severity",
  "Financial Exposure",
  "Claims / Failures",
  "Trend / Acceleration",
  "Customer Impact",
  "Population Exposure",
  "Containment Effectiveness",
  "Timing / Aging",
  "Recurrence",
  "Supplier",
  "Evidence / Governance",
  "Read-Across",
  "Human Initiated",
] as const;

export const LEVEL_LABEL: Record<number, string> = {
  1: "Level 1 · Attention",
  2: "Level 2 · Management Escalation",
  3: "Level 3 · Critical / Executive",
};

/** Governed lifecycle, in order. */
export const LIFECYCLE = [
  ["attention", "Attention"],
  ["open", "Escalated"],
  ["awaiting_decision", "Acknowledged · awaiting decision"],
  ["decided", "Decided"],
  ["actions_in_progress", "Actions"],
  ["effectiveness_verification", "Effectiveness"],
  ["closed", "Authorized closure"],
] as const;

export const STATUS_LABEL: Record<string, string> = {
  attention: "Attention — monitoring",
  open: "Escalated — awaiting acknowledgement",
  awaiting_decision: "Awaiting decision",
  decided: "Decided — actions pending",
  actions_in_progress: "Actions in progress",
  effectiveness_verification: "Effectiveness verification",
  closed: "Closed (authorized)",
};

export function triggerLabel(key: string): string {
  const k = key.replace(/^trigger\./, "").replace(/_/g, " ");
  return k.charAt(0).toUpperCase() + k.slice(1);
}

/** Formal management decision pending (Level 2/3, before a decision). */
export const isOpen = (e: Escalation) => e.status === "open" || e.status === "awaiting_decision";
/** Level 1 — surfaced and monitored, not yet a formal decision. */
export const isAttention = (e: Escalation) => e.status === "attention";
/** Decided but not yet closed: actions / effectiveness still outstanding. */
export const isPostDecision = (e: Escalation) =>
  e.status === "decided" ||
  e.status === "actions_in_progress" ||
  e.status === "effectiveness_verification";
export const isOverdue = (e: Escalation, now = Date.now()) =>
  isOpen(e) && !!e.due_at && new Date(e.due_at).getTime() < now;
/** Safety/regulatory immediate-response clock — independent of the decision clock. */
export const isResponseOverdue = (e: Escalation, now = Date.now()) =>
  e.response_requirement === "immediate" &&
  !e.acknowledged_at &&
  !!e.response_due_at &&
  new Date(e.response_due_at).getTime() < now;
export function isDueToday(e: Escalation, now = new Date()) {
  if (!isOpen(e) || !e.due_at) return false;
  const d = new Date(e.due_at);
  return d.toDateString() === now.toDateString() && d.getTime() >= now.getTime();
}
export const isNew = (e: Escalation, now = Date.now()) =>
  (isOpen(e) || isAttention(e)) && now - new Date(e.created_at).getTime() < 24 * 3600 * 1000;
export const isRecentlyDecided = (e: Escalation, days = 7, now = Date.now()) =>
  !!e.decided_at && now - new Date(e.decided_at).getTime() < days * 24 * 3600 * 1000;
export const isRecentlyClosed = (e: Escalation, days = 7, now = Date.now()) =>
  !!e.closed_at && now - new Date(e.closed_at).getTime() < days * 24 * 3600 * 1000;

export function fmtDateTime(iso: string | null | undefined) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function dueLabel(e: Escalation) {
  if (!e.due_at) return "No due date";
  const diff = new Date(e.due_at).getTime() - Date.now();
  const h = Math.round(Math.abs(diff) / 3600000);
  const span = h >= 48 ? `${Math.round(h / 24)}d` : `${h}h`;
  if (!isOpen(e)) return fmtDateTime(e.due_at);
  return diff < 0 ? `${span} overdue` : `due in ${span}`;
}

function normalize(row: Record<string, unknown>): Escalation {
  const r = row as unknown as Escalation;
  return {
    ...r,
    evidence_snapshot: (r.evidence_snapshot ?? {}) as EvidenceSnapshot,
    decision_evidence_snapshot: (r.decision_evidence_snapshot ?? {}) as EvidenceSnapshot,
    resulting_actions: Array.isArray(r.resulting_actions) ? (r.resulting_actions as string[]) : [],
    contributing_triggers: Array.isArray(r.contributing_triggers) ? r.contributing_triggers : [],
    required_participants: Array.isArray(r.required_participants) ? r.required_participants : [],
  };
}

export function useEscalations(recordId?: string) {
  const [escalations, setEscalations] = useState<Escalation[]>([]);
  const [events, setEvents] = useState<EscalationEvent[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    let q = supabase.from("escalations").select("*").order("created_at", { ascending: false });
    let ev = supabase
      .from("escalation_events")
      .select("*")
      .order("occurred_at", { ascending: true });
    if (recordId) {
      q = q.eq("record_id", recordId);
      ev = ev.eq("record_id", recordId);
    }
    const [a, b] = await Promise.all([q, ev]);
    setEscalations((a.data ?? []).map((r) => normalize(r as Record<string, unknown>)));
    setEvents((b.data ?? []) as EscalationEvent[]);
    setLoading(false);
  }, [recordId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { escalations, events, loading, refresh };
}

export async function acknowledgeEscalation(id: string, comment: string) {
  const { error } = await supabase.rpc("escalation_acknowledge", {
    p_escalation_id: id,
    p_comment: comment,
  });
  if (error) throw new Error(error.message);
}

export async function recordManagementDecision(args: {
  id: string;
  decision: string;
  rationale: string;
  actions: string[];
}) {
  const { error } = await supabase.rpc("escalation_record_decision", {
    p_escalation_id: args.id,
    p_decision: args.decision,
    p_rationale: args.rationale,
    p_actions: args.actions as never,
  });
  if (error) throw new Error(error.message);
}

async function rpc(name: string, args: Record<string, unknown>) {
  const { data, error } = await supabase.rpc(name as never, args as never);
  if (error) throw new Error(error.message);
  return data as unknown;
}

/** Rule-triggered: Broadview evaluates the approved thresholds + safety override server-side. */
export async function evaluateEscalationTriggers(recordId: string) {
  return (await rpc("escalation_evaluate_record", { p_record_id: recordId })) as {
    met: EscalationTrigger[];
    escalationId: string | null;
    level?: number;
  };
}

/** Human-initiated escalation by an authorized person, rationale required. */
export async function raiseHumanEscalation(args: {
  recordId: string;
  level: number;
  category: string;
  rationale: string;
  decisionRequired: string;
  safety: boolean;
}) {
  await rpc("escalation_raise_human", {
    p_record_id: args.recordId,
    p_level: args.level,
    p_category: args.category,
    p_rationale: args.rationale,
    p_decision_required: args.decisionRequired,
    p_safety: args.safety,
  });
}

export async function changeEscalationLevel(
  id: string,
  level: number,
  rationale: string,
  evidence: string[],
) {
  await rpc("escalation_change_level", {
    p_escalation_id: id,
    p_new_level: level,
    p_rationale: rationale,
    p_evidence: evidence,
  });
}

export async function advanceEscalationLifecycle(args: {
  id: string;
  to: "actions_in_progress" | "effectiveness_verification" | "closed";
  note: string;
  evidence?: string[];
  effective?: boolean | undefined;
  residualOk?: boolean | undefined;
}) {
  await rpc("escalation_advance_lifecycle", {
    p_escalation_id: args.id,
    p_to: args.to,
    p_note: args.note,
    p_evidence: args.evidence ?? [],
    p_effective: args.effective ?? null,
    p_residual_ok: args.residualOk ?? null,
  });
}

export interface EscalationThreshold {
  id: string;
  category: string;
  metric: string;
  level: number;
  threshold: number;
  unit: string;
  description: string;
  approval_status: string;
  active: boolean;
  effective_from: string;
}
export interface ThresholdChange {
  id: string;
  threshold_id: string;
  category: string;
  metric: string;
  level: number;
  old_value: number | null;
  new_value: number | null;
  rationale: string;
  changed_by_label: string;
  approved_by_label: string;
  effective_from: string;
  created_at: string;
}

export function useEscalationThresholds() {
  const [thresholds, setThresholds] = useState<EscalationThreshold[]>([]);
  const [changes, setChanges] = useState<ThresholdChange[]>([]);
  const [loading, setLoading] = useState(true);
  const refresh = useCallback(async () => {
    const [a, b] = await Promise.all([
      supabase.from("escalation_trigger_thresholds").select("*").order("category").order("level"),
      supabase
        .from("escalation_threshold_changes")
        .select("*")
        .order("created_at", { ascending: false }),
    ]);
    setThresholds((a.data ?? []) as EscalationThreshold[]);
    setChanges((b.data ?? []) as ThresholdChange[]);
    setLoading(false);
  }, []);
  useEffect(() => {
    void refresh();
  }, [refresh]);
  return { thresholds, changes, loading, refresh };
}

export async function setEscalationThreshold(
  id: string,
  value: number,
  rationale: string,
  effectiveFrom?: string,
) {
  await rpc("escalation_set_threshold", {
    p_threshold_id: id,
    p_new_value: value,
    p_active: null,
    p_rationale: rationale,
    p_effective_from: effectiveFrom ?? null,
  });
}
